import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Check, Play, RefreshCw, SearchCheck, X } from "lucide-react";
import { supabase } from "../lib/supabase";
import {
  buildDiscoveryCreatePayload,
  buildDiscoveryLaunchPayload,
  buildDiscoveryRunSummary,
  formatDiscoveryConfidence,
  getQuickMessageDiscoveryStatusLabel,
  getQuickMessageDiscoveryStatusTone,
  isDiscoveryRunActive,
} from "../lib/quickMessageDiscovery.mjs";

const API_URL = import.meta.env.VITE_API_URL;

type ClinicRow = {
  id: string;
  raison_sociale?: string | null;
};

type DiscoveryRun = {
  id: string;
  clinic_id: string;
  status: string;
  period_start: string;
  period_end: string;
  created_at?: string | null;
  completed_at?: string | null;
  source_conversation_count?: number | null;
  source_thread_count?: number | null;
  patient_signal_count?: number | null;
  human_response_signal_count?: number | null;
  suggestion_count?: number | null;
  error_message?: string | null;
};

type DiscoverySuggestion = {
  id: string;
  run_id: string;
  clinic_id: string;
  rank: number;
  status: string;
  display_name: string;
  shortcut: string;
  template_body: string;
  confidence: number;
  source_type: string;
  signal_summary_json?: {
    support_thread_count?: number;
    support_message_count?: number;
    scenario_label?: string;
    examples?: string[];
  } | null;
};

type RunDetail = {
  run: DiscoveryRun;
  suggestions: DiscoverySuggestion[];
};

type EditingState = {
  suggestion: DiscoverySuggestion;
  displayName: string;
  shortcut: string;
  templateBody: string;
};

function toDateInputValue(date: Date) {
  return date.toISOString().slice(0, 10);
}

function formatDateTime(value?: string | null) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("en-SG", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function toneClass(status: string) {
  const tone = getQuickMessageDiscoveryStatusTone(status);
  if (tone === "success") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (tone === "warning") return "bg-amber-50 text-amber-700 border-amber-200";
  if (tone === "danger") return "bg-red-50 text-red-700 border-red-200";
  if (tone === "progress") return "bg-blue-50 text-blue-700 border-blue-200";
  return "bg-slate-50 text-slate-600 border-slate-200";
}

function sourceLabel(sourceType: string) {
  if (sourceType === "patient_questions") return "Patient questions";
  if (sourceType === "human_responses") return "Human responses";
  if (sourceType === "mixed") return "Patient + team";
  return "Signal";
}

export default function QuickMessageDiscovery() {
  const today = useMemo(() => new Date(), []);
  const weekAgo = useMemo(() => {
    const date = new Date(today);
    date.setDate(date.getDate() - 7);
    return date;
  }, [today]);
  const [clinics, setClinics] = useState<ClinicRow[]>([]);
  const [runs, setRuns] = useState<DiscoveryRun[]>([]);
  const [selectedDetail, setSelectedDetail] = useState<RunDetail | null>(null);
  const [clinicId, setClinicId] = useState("");
  const [periodStart, setPeriodStart] = useState(toDateInputValue(weekAgo));
  const [periodEnd, setPeriodEnd] = useState(toDateInputValue(today));
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [savingSuggestion, setSavingSuggestion] = useState(false);

  const clinicNameById = useMemo(
    () =>
      new Map(
        clinics.map((clinic) => [
          clinic.id,
          clinic.raison_sociale?.trim() || clinic.id,
        ])
      ),
    [clinics]
  );

  async function getAuthHeaders() {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;
    if (!token) throw new Error("Missing auth token");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  async function fetchAdminJson(path: string, options: RequestInit = {}) {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}${path}`, {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {}),
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.detail || "Request failed");
    }
    return data;
  }

  async function loadClinics() {
    const { data, error } = await supabase
      .from("clients")
      .select("id, raison_sociale")
      .order("raison_sociale");
    if (error) throw error;
    const rows = (data ?? []) as ClinicRow[];
    setClinics(rows);
    if (!clinicId && rows[0]?.id) setClinicId(rows[0].id);
  }

  async function loadRuns() {
    const data = await fetchAdminJson("/admin/quick-message-discovery/runs");
    setRuns((data.runs ?? []) as DiscoveryRun[]);
  }

  async function loadRunDetail(runId: string, { silent = false } = {}) {
    if (!silent) setDetailLoading(true);
    try {
      const data = await fetchAdminJson(`/admin/quick-message-discovery/runs/${runId}`);
      setSelectedDetail(data as RunDetail);
    } finally {
      if (!silent) setDetailLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    async function loadInitialData() {
      try {
        setLoading(true);
        await loadClinics();
        if (!cancelled) await loadRuns();
      } catch (err) {
        console.error("[quick-message-discovery] load failed", err);
        toast.error("Failed to load discovery data");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadInitialData();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const runId = selectedDetail?.run?.id;
    const status = selectedDetail?.run?.status;
    if (!runId || !isDiscoveryRunActive(status)) return;
    const timer = window.setInterval(() => {
      loadRunDetail(runId, { silent: true }).catch((err) => {
        console.error("[quick-message-discovery] poll failed", err);
      });
      loadRuns().catch(() => undefined);
    }, 3500);
    return () => window.clearInterval(timer);
  }, [selectedDetail?.run?.id, selectedDetail?.run?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleLaunch() {
    const payload = buildDiscoveryLaunchPayload({
      clinicId,
      periodStart,
      periodEnd,
    });
    if (!payload.clinic_id || !payload.period_start || !payload.period_end) {
      toast.error("Clinic and period are required");
      return;
    }

    try {
      setLaunching(true);
      const data = await fetchAdminJson("/admin/quick-message-discovery/runs", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      toast.success("Analysis started");
      await loadRuns();
      await loadRunDetail(data.id);
    } catch (err) {
      console.error("[quick-message-discovery] launch failed", err);
      toast.error(err instanceof Error ? err.message : "Failed to start analysis");
    } finally {
      setLaunching(false);
    }
  }

  async function handleIgnore(suggestion: DiscoverySuggestion) {
    try {
      await fetchAdminJson(`/admin/quick-message-discovery/suggestions/${suggestion.id}/ignore`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      toast.success("Suggestion ignored");
      await loadRunDetail(suggestion.run_id);
      await loadRuns();
    } catch (err) {
      console.error("[quick-message-discovery] ignore failed", err);
      toast.error("Failed to ignore suggestion");
    }
  }

  function openCreatePanel(suggestion: DiscoverySuggestion) {
    setEditing({
      suggestion,
      displayName: suggestion.display_name || "",
      shortcut: suggestion.shortcut || "",
      templateBody: suggestion.template_body || "",
    });
  }

  async function handleCreateQuickMessage() {
    if (!editing) return;
    const payload = buildDiscoveryCreatePayload(editing);
    if (!payload.display_name || !payload.shortcut || !payload.template_body) {
      toast.error("Display name, shortcut and body are required");
      return;
    }

    try {
      setSavingSuggestion(true);
      await fetchAdminJson(
        `/admin/quick-message-discovery/suggestions/${editing.suggestion.id}/create-quick-message`,
        {
          method: "POST",
          body: JSON.stringify(payload),
        }
      );
      toast.success("Quick message created");
      setEditing(null);
      await loadRunDetail(editing.suggestion.run_id);
      await loadRuns();
    } catch (err) {
      console.error("[quick-message-discovery] create failed", err);
      toast.error(err instanceof Error ? err.message : "Failed to create quick message");
    } finally {
      setSavingSuggestion(false);
    }
  }

  const selectedRun = selectedDetail?.run ?? null;
  const selectedSuggestions = selectedDetail?.suggestions ?? [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Quick discovery</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Launch platform-admin analysis to find editable quick-message opportunities from patient questions and team replies.
          </p>
        </div>
        <button
          onClick={() => {
            loadRuns().catch(() => toast.error("Failed to refresh runs"));
            if (selectedRun) loadRunDetail(selectedRun.id).catch(() => undefined);
          }}
          className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1.6fr)_minmax(150px,0.7fr)_minmax(150px,0.7fr)_auto] lg:items-end">
          <label className="space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Clinic</span>
            <select
              value={clinicId}
              onChange={(event) => setClinicId(event.target.value)}
              disabled={loading || clinics.length === 0}
              className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 focus:border-teal-500 focus:outline-none"
            >
              {clinics.map((clinic) => (
                <option key={clinic.id} value={clinic.id}>
                  {clinic.raison_sociale?.trim() || clinic.id}
                </option>
              ))}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">Start</span>
            <input
              type="date"
              value={periodStart}
              onChange={(event) => setPeriodStart(event.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-teal-500 focus:outline-none"
            />
          </label>
          <label className="space-y-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">End</span>
            <input
              type="date"
              value={periodEnd}
              onChange={(event) => setPeriodEnd(event.target.value)}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-teal-500 focus:outline-none"
            />
          </label>
          <button
            onClick={handleLaunch}
            disabled={launching || loading || !clinicId}
            className="inline-flex min-h-[38px] items-center justify-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Play size={16} />
            {launching ? "Starting..." : "Run analysis"}
          </button>
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(300px,0.85fr)_minmax(0,1.35fr)]">
        <section className="min-h-[360px] rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-900">Recent runs</h2>
          </div>
          {loading ? (
            <p className="px-4 py-6 text-sm text-slate-500">Loading runs...</p>
          ) : runs.length === 0 ? (
            <p className="px-4 py-6 text-sm text-slate-500">No discovery runs yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {runs.map((run) => {
                const active = selectedRun?.id === run.id;
                return (
                  <button
                    key={run.id}
                    onClick={() => loadRunDetail(run.id).catch(() => toast.error("Failed to load run"))}
                    className={`w-full px-4 py-3 text-left transition ${
                      active ? "bg-teal-50/70" : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900">
                          {clinicNameById.get(run.clinic_id) || run.clinic_id}
                        </p>
                        <p className="mt-1 text-xs text-slate-500">{buildDiscoveryRunSummary(run)}</p>
                        <p className="mt-1 text-xs text-slate-400">{formatDateTime(run.created_at)}</p>
                      </div>
                      <span className={`shrink-0 rounded-full border px-2 py-0.5 text-xs ${toneClass(run.status)}`}>
                        {getQuickMessageDiscoveryStatusLabel(run.status)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="min-h-[360px] rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-900">Run detail</h2>
          </div>
          {detailLoading ? (
            <p className="px-4 py-6 text-sm text-slate-500">Loading run detail...</p>
          ) : !selectedRun ? (
            <div className="flex min-h-[300px] flex-col items-center justify-center px-6 text-center text-slate-500">
              <SearchCheck className="mb-3 h-8 w-8 text-slate-300" />
              <p className="text-sm">Select a run or launch a new analysis.</p>
            </div>
          ) : (
            <div className="space-y-5 p-4">
              <div className="flex flex-col gap-3 border-b border-slate-100 pb-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-base font-semibold text-slate-900">
                    {clinicNameById.get(selectedRun.clinic_id) || selectedRun.clinic_id}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    {formatDateTime(selectedRun.period_start)} to {formatDateTime(selectedRun.period_end)}
                  </p>
                  <p className="mt-1 text-xs text-slate-400">{buildDiscoveryRunSummary(selectedRun)}</p>
                </div>
                <span className={`w-fit rounded-full border px-2.5 py-1 text-xs ${toneClass(selectedRun.status)}`}>
                  {getQuickMessageDiscoveryStatusLabel(selectedRun.status)}
                </span>
              </div>

              {selectedRun.error_message && (
                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {selectedRun.error_message}
                </div>
              )}

              {selectedRun.status === "not_enough_signal" ? (
                <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                  No reliable quick-message suggestions were found for this period.
                </p>
              ) : selectedSuggestions.length === 0 ? (
                <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
                  Suggestions will appear here when the run is ready.
                </p>
              ) : (
                <div className="space-y-3">
                  {selectedSuggestions.map((suggestion) => (
                    <article key={suggestion.id} className="rounded-lg border border-slate-200 p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-sm font-semibold text-slate-900">{suggestion.display_name}</p>
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600">
                              /{suggestion.shortcut}
                            </span>
                            <span className="rounded-full bg-teal-50 px-2 py-0.5 text-xs text-teal-700">
                              {sourceLabel(suggestion.source_type)}
                            </span>
                          </div>
                          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                            {suggestion.template_body}
                          </p>
                          <p className="mt-3 text-xs text-slate-500">
                            {formatDiscoveryConfidence(suggestion.confidence)} confidence
                            {suggestion.signal_summary_json?.support_thread_count
                              ? ` · ${suggestion.signal_summary_json.support_thread_count} supporting threads`
                              : ""}
                            {suggestion.signal_summary_json?.support_message_count
                              ? ` · ${suggestion.signal_summary_json.support_message_count} messages`
                              : ""}
                          </p>
                        </div>
                        <div className="flex shrink-0 gap-2">
                          <button
                            onClick={() => openCreatePanel(suggestion)}
                            disabled={suggestion.status !== "suggested"}
                            className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 py-2 text-xs font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Check size={14} />
                            Create
                          </button>
                          <button
                            onClick={() => handleIgnore(suggestion)}
                            disabled={suggestion.status !== "suggested"}
                            className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <X size={14} />
                            Ignore
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex">
          <button
            aria-label="Close quick message editor"
            className="flex-1 bg-slate-950/30"
            onClick={() => setEditing(null)}
          />
          <aside className="flex h-full w-full max-w-xl flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">Create quick message</p>
                <p className="text-xs text-slate-500">Edit before saving to the clinic.</p>
              </div>
              <button
                onClick={() => setEditing(null)}
                className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-slate-700">Display name</span>
                <input
                  value={editing.displayName}
                  onChange={(event) => setEditing({ ...editing, displayName: event.target.value })}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none"
                />
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-slate-700">Shortcut</span>
                <div className="flex items-center rounded-md border border-slate-200 focus-within:border-teal-500">
                  <span className="px-3 font-mono text-sm text-slate-400">/</span>
                  <input
                    value={editing.shortcut}
                    onChange={(event) => setEditing({ ...editing, shortcut: event.target.value })}
                    className="w-full rounded-r-md py-2 pr-3 font-mono text-sm focus:outline-none"
                  />
                </div>
              </label>
              <label className="block space-y-1.5">
                <span className="text-sm font-medium text-slate-700">Message body</span>
                <textarea
                  value={editing.templateBody}
                  onChange={(event) => setEditing({ ...editing, templateBody: event.target.value })}
                  rows={9}
                  className="w-full resize-none rounded-md border border-slate-200 px-3 py-2 text-sm leading-relaxed focus:border-teal-500 focus:outline-none"
                />
              </label>
            </div>
            <div className="flex justify-end gap-2 border-t border-slate-200 px-6 py-4">
              <button
                onClick={() => setEditing(null)}
                className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateQuickMessage}
                disabled={savingSuggestion}
                className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {savingSuggestion ? "Creating..." : "Create quick message"}
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
